import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import $ from "jquery";

export default function EkycComponent1({ onBack }) {
    const merchantdetails = useSelector(
        (state) => state.auth?.employeeDetails
    );

    const { lat, lng } = useSelector(
        (state) => state.location
    );

    const username = merchantdetails?.username;

    const {
        captureFingerprint,
        loading: rdLoading,
    } = useRDService();

    // ================= APIs =================

    // NEW API: Check eKYC Status
    const { callApi: checkEkycApi } = useApi(
        `/aeps/fingpay/ekyc-status/${username || ""}`,
        "GET"
    );

    const { callApi: createMerchantApi } = useApi(
        "/v1/fingpay/merchant/create",
        "POST"
    );

    const { callApi: sendOtpApi } = useApi(
        "/v1/fingpay/ekyc/send-otp",
        "POST"
    );

    const { callApi: validateOtpApi } = useApi(
        "/v1/fingpay/ekyc/validate-otp",
        "POST"
    );

    const { callApi: resendOtpApi } = useApi(
        "/v1/fingpay/ekyc/resend-otp",
        "POST"
    );

    const { callApi: biometricApi } = useApi(
        "/v1/fingpay/ekyc/biometric",
        "POST"
    );

    // ================= STATES =================

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [otp, setOtp] = useState("");
    const [merchantData, setMerchantData] = useState(null);
    const [showOnlyBio, setShowOnlyBio] = useState(false);
    const [biometricCompleted, setBiometricCompleted] = useState(false);

    // Tracks if we have been redirected for Bank Verification
    const [isBankVerification, setIsBankVerification] = useState(false);

    // Mobile number input for merchant create
    const [mobileNumber, setMobileNumber] = useState("");
    const [showMobileForm, setShowMobileForm] = useState(true);

    // ================= FORM =================

    const [form, setForm] = useState({
        name: merchantdetails?.name || "",
        mobile: merchantdetails?.mobile_number || "",
        email: merchantdetails?.email || "",
        pan: merchantdetails?.pan || "",
        aadhaar: merchantdetails?.aadhaar_number || "",
        dob: merchantdetails?.dob || "",
        gender: merchantdetails?.gender || "",
        address: merchantdetails?.address?.full || "",
        city: merchantdetails?.address?.city || "",
        pincode: merchantdetails?.address?.pincode || ""
    });

    // ================= USE EFFECT =================

    useEffect(() => {
        if (username) {
            verifyEkycStatus();
        }
    }, [username]);

    // ================= STATUS VERIFICATION =================

    const verifyEkycStatus = async () => {
        try {
            setLoading(true);
            const statusRes = await checkEkycApi(null);

            console.log("New Status API Response =>", statusRes);

            if (statusRes?.ekycDone === true) {
                setBiometricCompleted(true);
                setShowMobileForm(false);
                setMessage("Merchant Activated Successfully");
                setMerchantData({
                    data: {
                        merchantLoginId: statusRes?.mid
                    }
                });
            }
            // If ekycDone is false, mobile form stays visible (default true)
        } catch (error) {
            console.error("Status API check failed", error);
            // Mobile form stays visible on error
        } finally {
            setLoading(false);
        }
    };

    // ================= CREATE MERCHANT (ORIGINAL LOGIC) =================

    const checkMerchantStatus = async (mob) => {
        try {
            setLoading(true);
            const res = await createMerchantApi(
                null,
                `?username=${username}&mobileNumber=${mob}`
            );

            console.log("Merchant Response =>", res);
            setMerchantData(res);

            if ((res?.statusCode === 10000 ||res?.statusCode === 200) && res?.data?.merchantStatus === true) {
                setBiometricCompleted(true);
                setMessage("Merchant Activated Successfully");
            }
        } catch (error) {
            console.log(error);
            setMessage(error?.message || "Merchant check failed");
        } finally {
            setLoading(false);
        }
    };

    // ================= CREATE MERCHANT (MANUAL TRIGGER) =================

    const handleCreateMerchant = async () => {
        if (!mobileNumber || mobileNumber.trim().length < 10) {
            setMessage("Please enter a valid 10-digit mobile number");
            return;
        }
        setMessage("");

        // Check eKYC status first — if already completed, skip create merchant API
        try {
            setLoading(true);
            const statusRes = await checkEkycApi(null);
            console.log("Status API Response =>", statusRes);

            if (statusRes?.ekycDone === true) {
                setBiometricCompleted(true);
                setMessage("Merchant Activated Successfully");
                setMerchantData({ data: { merchantLoginId: statusRes?.mid } });
                setShowMobileForm(false);
                setLoading(false);
                return;
            }
        } catch (error) {
            console.error("Status check failed, proceeding to create merchant", error);
        } finally {
            setLoading(false);
        }

        // eKYC not done — call create merchant API with entered mobile number
        setShowMobileForm(false);
        await checkMerchantStatus(mobileNumber.trim());
    };

    // ================= PID XML PARSER =================

    const parsePidXml = (xml) => {
        const $xml = $(xml);

        const get = (selector, attr) =>
            $xml.find(selector).attr(attr) || "0";

        const infoValue = (name) =>
            $xml.find(`Info[name='${name}']`).attr("value") ||
            $xml.find(`Param[name='${name}']`).attr("value") ||
            "0";

        return {
            dc: get("DeviceInfo", "dc"),
            ci: get("Skey", "ci"),
            hmac: $xml.find("Hmac").text() || "0",
            dpID: get("DeviceInfo", "dpId"),
            mc: get("DeviceInfo", "mc"),
            PidDatatype: get("Data", "type"),
            sessionKey: $xml.find("Skey").text() || "0",
            mi: get("DeviceInfo", "mi"),
            rdsID: get("DeviceInfo", "rdsId"),
            errCode: get("Resp", "errCode"),
            errInfo: get("Resp", "errInfo"),
            fCount: get("Resp", "fCount"),
            fType: get("Resp", "fType"),
            iCount: get("Resp", "iCount"),
            iType: get("Resp", "iType"),
            pCount: get("Resp", "pCount"),
            pType: "0",
            srno: infoValue("srno"),
            sysid: infoValue("sysid"),
            ts: infoValue("ts"),
            Piddata: $xml.find("Data").text() || "0",
            qScore: get("Resp", "qScore"),
            nmPoints: get("Resp", "nmPoints"),
            rdsVer: get("DeviceInfo", "rdsVer"),
        };
    };

    // ================= SEND OTP =================

    const handleSendOtp = async () => {
        try {
            setLoading(true);
            const res = await sendOtpApi(null, `?username=${username}&mobileNumber=${mobileNumber}`);
            console.log(res);

            if (res?.statusCode === 10000) {
                setMessage("OTP Sent Successfully");
            } else {
                setMessage(res?.message || "OTP Send Failed");
            }
        } catch (error) {
            console.log(error);
            setMessage(error?.message || "OTP send failed");
        } finally {
            setLoading(false);
        }
    };

    // ================= VALIDATE OTP =================

    const handleValidateOtp = async () => {
        try {
            if (!otp) {
                return setMessage("Please enter OTP");
            }

            setLoading(true);
            const res = await validateOtpApi(
                null,
                `?username=${username}&otp=${otp}`
            );

            console.log(res);

            if (res?.statusCode === 10000) {
                setMessage("OTP Verified Successfully");
            } else if (res?.statusCode === 999) {
                setShowOnlyBio(true);
                setMessage("OTP Verified Successfully. Please complete biometric.");
            } else {
                setMessage(res?.message || "OTP Verification Failed");
            }

            setOtp("");
        } catch (error) {
            console.log(error);
            setMessage(error?.message || "OTP validation failed");
        } finally {
            setLoading(false);
        }
    };

    // ================= RESEND OTP =================

    const handleResendOtp = async () => {
        try {
            setLoading(true);
            const res = await resendOtpApi(null, `?username=${username}&mobileNumber=${mobileNumber}`);
            console.log(res);

            if (res?.statusCode === 10000) {
                setMessage("OTP Sent Successfully");
            } else {
                setMessage(res?.message || "OTP resend failed");
            }
        } catch (error) {
            console.log(error);
            setMessage(error?.message || "OTP resend failed");
        } finally {
            setLoading(false);
        }
    };

    // ================= BIOMETRIC =================

    const handleBiometricSubmit = async () => {
        try {
            setLoading(true);
            const wadhvalue = "E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=";

            // Capture Fingerprint
            const pidXml = await captureFingerprint(wadhvalue);

            if (!pidXml) {
                return setMessage("Fingerprint capture failed");
            }

            // Parse XML
            const payload = parsePidXml(pidXml);

            // API CALL
            const res = await biometricApi(
                payload,
                `?username=${username}`
            );

            console.log("Biometric Response =>", res);

            // SUCCESS
            if (
                res?.statusCode === 10000 &&
                res?.message?.includes("Bank eKyc successfully completed")
            ) {
                setBiometricCompleted(true);
                setMessage("Bank eKyc successfully completed");
                verifyEkycStatus(); // Re-verify with the new API to fetch the accurate MID if needed

                if (onBack) {
                    setTimeout(() => {
                        onBack();
                    }, 1500);
                }
                return;
            }

            // OTP AGAIN REQUIRED
            if (
                res?.statusCode === 10000 &&
                (res?.message?.includes("Please complete Bank eKYC") || 
                 res?.message?.includes("Please Send & validate OTP before giving BioMetric") ||
                 res?.message === "SUCCESS")
            ) {
                setShowOnlyBio(false);
                setIsBankVerification(true);
                setMessage("Please verify OTP again");
                return;
            }

            // OTHER RESPONSE
            setMessage(res?.message || "Biometric failed");

        } catch (error) {
            console.log(error);
            setMessage(error?.message || "Biometric failed");
        } finally {
            setLoading(false);
        }
    };

    // ================= UI =================

    return (
        <div className="overflow-hidden bg-gray-100 p-4 flex items-center">
            <div className="w-full bg-white rounded-2xl shadow-xl p-6">
                {/* HEADER */}
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <button
                        onClick={onBack}
                        className="px-4 py-2 bg-gray-100 rounded-lg"
                    >
                        ← Back to AEPS
                    </button>
                </div>

                {/* TITLE */}
                <h2 className="text-2xl font-semibold mb-6">
                    {isBankVerification ? "Bank Verification" : "E-KYC Verification"}
                </h2>

                {/* LOADING */}
                {loading && (
                    <div className="text-center py-3">
                        Processing...
                    </div>
                )}

                {/* MOBILE FORM */}
                {showMobileForm && !biometricCompleted && (
                    <div className="mb-6">
                        <p className="text-sm text-gray-600 mb-3 font-medium">
                            Enter your mobile number to create merchant account
                        </p>
                        <div className="flex gap-3">
                            <input
                                type="tel"
                                maxLength={10}
                                placeholder="Enter 10-digit Mobile Number"
                                value={mobileNumber}
                                onChange={(e) =>
                                    setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
                                }
                                onKeyDown={(e) => e.key === "Enter" && handleCreateMerchant()}
                                className="flex-1 border border-gray-300 rounded-xl px-4 py-3 outline-none focus:border-indigo-500"
                            />
                            <button
                                onClick={handleCreateMerchant}
                                disabled={loading}
                                className="bg-[#12345A] hover:bg-[#12345A]/90 text-white px-6 rounded-xl font-medium disabled:opacity-50 transition-all"
                            >
                                Create Merchant
                            </button>
                        </div>
                    </div>
                )}

                {/* SUCCESS */}
                {biometricCompleted ? (
                    <div className="text-center py-10">
                        <h3 className="text-2xl text-green-600 mb-6">
                            ✅ Merchant Activated Successfully
                        </h3>
                        <div className="bg-green-50 rounded-xl p-6">
                            <p>Merchant onboarding completed successfully</p>
                            <p className="mt-2">
                                Merchant ID :{" "}
                                {merchantData?.data?.merchantLoginId}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* MOBILE NUMBER DISPLAY */}
                        {!showMobileForm && mobileNumber && (
                            <div className="flex items-center gap-2 mb-4 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                                <span className="text-sm text-gray-500">Mobile Number:</span>
                                <span className="font-semibold text-gray-800">{mobileNumber}</span>
                            </div>
                        )}

                        {/* OTP INPUT */}
                        {!showMobileForm && !showOnlyBio && (
                            <div className="flex gap-3 mb-4">
                                <input
                                    type="text"
                                    placeholder="Enter OTP"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    className="flex-1 border border-gray-300 rounded-xl px-4 py-3 outline-none"
                                />
                                <button
                                    onClick={handleValidateOtp}
                                    disabled={loading}
                                    className="bg-indigo-600 text-white px-5 rounded-xl"
                                >
                                    Verify
                                </button>
                            </div>
                        )}

                        {/* BUTTONS */}
                        {!showMobileForm && (
                            <div className="grid grid-cols-2 gap-4">
                                {!showOnlyBio && (
                                    <>
                                        <button
                                            onClick={handleSendOtp}
                                            disabled={loading}
                                            className="bg-[#12345A] hover:bg-[#12345A]/90 text-white py-3 rounded-xl font-medium transition-all disabled:opacity-50"
                                        >
                                            Send OTP
                                        </button>
                                        <button
                                            onClick={handleResendOtp}
                                            disabled={loading}
                                            className="border-2 border-[#12345A] text-[#12345A] hover:bg-slate-50 py-3 rounded-xl font-medium transition-all disabled:opacity-50"
                                        >
                                            Resend OTP
                                        </button>
                                    </>
                                )}

                                <button
                                    onClick={handleBiometricSubmit}
                                    disabled={loading || rdLoading}
                                    className="col-span-2 bg-[#12345A] hover:bg-[#12345A]/90 text-white py-3.5 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md mt-2"
                                >
                                    {rdLoading
                                        ? "Capturing Fingerprint..."
                                        : "Complete Biometric eKYC"}
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* MESSAGE */}
                {message && (
                    <div className="mt-5 p-4 bg-gray-50 rounded-xl text-center">
                        {message}
                    </div>
                )}
            </div>
        </div>
    );
}

// ================= INPUT =================

function Input({ label, ...props }) {
    return (
        <div>
            <label className="block mb-1">{label}</label>
            <input
                {...props}
                className="w-full border rounded-lg p-3 bg-gray-50"
            />
        </div>
    );
}